import React, { FC } from 'react'
import { Controller, ControllerProps } from 'react-hook-form'
import { Select, ISelectOption } from 'components/forms/Selects'
import { ISelectProps as DeprecatedISelectProps } from 'components/forms/Selects/Select'
import { ISelectProps } from 'transfix-ui/components/Select/Select'
import { SelectProps as MuiSelectProps } from 'transfix-ui/core'
import { Select as MuiSelect } from 'transfix-ui'

type IBaseDeprecatedControllerProps = ControllerProps<React.ComponentType<DeprecatedISelectProps>>

export interface IDeprecatedControlledSelectProps
  extends Pick<IBaseDeprecatedControllerProps, 'control' | 'name' | 'defaultValue'> {
  onChange?: (value: string | number) => void
  SelectProps?: Partial<DeprecatedISelectProps>
}

/**
 * Helper component for use with React Hook Form
 *
 * Note: onChange is used to call a user provided callback.
 * It has read-only access to the changed value but cannot be used to transform the value passed to render.
 *
 * @see https://react-hook-form.com/api#Controller
 * @example
 * ```
 * <DeprecatedControlledSelect
 *   control={control}
 *   defaultValue={defaultValue}
 *   name={name}
 *   onChange={onChange},
 *   SelectProps={{
 *     label: 'Unit type',
 *     options: unitTypeOptions,
 *  }}
 * />
 * ```
 */
/** @deprecated */
export const DeprecatedControlledSelect: FC<IDeprecatedControlledSelectProps> = ({
  control,
  name,
  defaultValue,
  onChange: userOnChange,
  SelectProps = {},
}) => {
  const {
    large = false,
    disabled = false,
    invalid = false,
    label,
    dataTest,
    options = [],
    placeholder,
    validationMessage,
    required,
    className,
  } = SelectProps || {}
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      render={({ onChange, value }) => {
        const changeHandler = (change: ISelectOption) => {
          const { value: newValue } = change || {}
          onChange(newValue)
          if (userOnChange) {
            userOnChange(newValue)
          }
        }

        return (
          <Select
            dataTest={dataTest}
            disabled={disabled}
            label={label}
            large={large}
            onChange={changeHandler}
            options={options}
            value={value}
            placeholder={placeholder}
            invalid={invalid}
            validationMessage={validationMessage}
            required={required}
            className={className}
          />
        )
      }}
    />
  )
}

type IBaseControllerProps = ControllerProps<React.ComponentType<ISelectProps>>

export interface IControlledSelectProps
  extends Pick<IBaseControllerProps, 'control' | 'name' | 'defaultValue'> {
  onChange?: MuiSelectProps['onChange']
  SelectProps?: Partial<ISelectProps>
}

/**
 * Helper component for use with React Hook Form
 *
 * @see https://react-hook-form.com/api#Controller
 * @example
 * ```
 * <ControlledSelect
 *   control={control}
 *   defaultValue={defaultValue}
 *   name={name}
 *   SelectProps={{
 *     label: 'Unit type',
 *     options: unitTypeOptions,
 *  }}
 * />
 * ```
 */
const ControlledSelect: FC<IControlledSelectProps> = ({
  control,
  name,
  defaultValue,
  onChange: onChangeProp,
  SelectProps = {},
}) => (
  <Controller
    control={control}
    name={name}
    defaultValue={defaultValue}
    render={({ onBlur, onChange, value: val }) => {
      const changeHandler: MuiSelectProps['onChange'] = (event, child) => {
        if (onChangeProp) {
          onChangeProp(event, child)
        }

        onChange(event.target.value)
      }

      return (
        <MuiSelect
          onBlur={onBlur}
          options={[]}
          {...SelectProps}
          value={val}
          onChange={changeHandler}
          error={SelectProps.error}
        />
      )
    }}
  />
)

export default ControlledSelect
